import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Clock, CheckCircle2, XCircle, Loader2, Landmark, Phone, Pencil, Upload } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const METHODS = [
  { v: "eft", l: "EFT to bank" },
  { v: "card", l: "Card payout" },
  { v: "ewallet", l: "E-wallet" },
  { v: "airtime", l: "Airtime" },
  { v: "voucher", l: "Voucher" },
];
const VOUCHER_TYPES = ["1Voucher", "Shopping", "Grocery", "Fuel", "Data bundle"];
const statusMap = {
  pending: { icon: Clock, cls: "bg-brand-orange/10 text-brand-orange" },
  processing: { icon: Loader2, cls: "bg-blue-500/10 text-blue-500" },
  processed: { icon: CheckCircle2, cls: "bg-primary/10 text-primary" },
  rejected: { icon: XCircle, cls: "bg-red-500/10 text-red-500" },
};

function toBase64(file) {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
}

function ChangeDetailsDialog({ profile, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ bank_name: "", bank_account: "", phone: "", note: "", document: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({ bank_name: profile.bank_name || "", bank_account: profile.bank_account || "", phone: profile.phone || "", note: "", document: "" });
  }, [open, profile]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/worker/change-request", form);
      toast.success("Change request sent to admin for approval");
      setOpen(false); onSubmitted();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 rounded-full" data-testid="request-change-button"><Pencil className="h-3.5 w-3.5" /> Request change</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">Request detail change</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-xs text-muted-foreground">Updates are submitted to admin for approval before they take effect.</p>
          <div><Label>Bank name</Label><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="cr-bank-input" className="mt-1" /></div>
          <div><Label>Account number</Label><Input value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} data-testid="cr-account-input" className="mt-1" /></div>
          <div><Label>Phone number</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cr-phone-input" className="mt-1" /></div>
          <div><Label>Note (optional)</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1" rows={2} /></div>
          <div>
            <Label>Proof document (optional)</Label>
            <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground" data-testid="cr-upload">
              <Upload className="h-4 w-4" /> {form.document ? "Document attached" : "Upload proof of bank account"}
              <input type="file" accept="image/*,.pdf" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (f) { const doc = await toBase64(f); setForm((s) => ({ ...s, document: doc })); } }} />
            </label>
          </div>
          <DialogFooter><Button type="submit" disabled={busy} data-testid="cr-submit" className="rounded-full bg-brand-navy font-semibold text-white hover:bg-brand-navy/90">{busy ? "Sending…" : "Submit to admin"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function WorkerCashout() {
  const MIN = 20;
  const [profile, setProfile] = useState(null);
  const [balance, setBalance] = useState(0);
  const [history, setHistory] = useState([]);
  const [requests, setRequests] = useState([]);
  const [type, setType] = useState("eft");
  const [amount, setAmount] = useState("");
  const [airtimeTarget, setAirtimeTarget] = useState("own");
  const [otherNumber, setOtherNumber] = useState("");
  const [voucherType, setVoucherType] = useState("1Voucher");
  const [voucherQty, setVoucherQty] = useState(1);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/worker/profile").then((r) => { setProfile(r.data); setBalance(r.data.wallet_balance || 0); setAmount((prev) => prev || String((r.data.wallet_balance || 0).toFixed(2))); }).catch(() => {});
    api.get("/worker/cashouts").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/worker/change-requests").then((r) => setRequests(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const isVoucher = type === "voucher";
  const isAirtime = type === "airtime";
  const computedAmount = isVoucher ? 100 * Number(voucherQty || 0) : Number(amount || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (computedAmount < MIN) return toast.error(`Minimum cashout is R${MIN}`);
    if (computedAmount > balance) return toast.error("Amount exceeds your wallet balance");
    setBusy(true);
    try {
      const payload = { amount: computedAmount, type };
      if (type === "eft" || type === "card") payload.destination = `${profile.bank_name || ""} ${profile.bank_account || ""}`.trim();
      if (type === "ewallet") payload.destination = profile.phone;
      if (isAirtime) { payload.recipient_number = airtimeTarget === "own" ? profile.phone : otherNumber; payload.airtime_amount = computedAmount; payload.destination = payload.recipient_number; }
      if (isVoucher) { payload.voucher_type = voucherType; payload.voucher_denomination = 100; payload.voucher_qty = Number(voucherQty); }
      await api.post("/worker/cashout", payload);
      toast.success("Cashout requested — pending admin approval");
      setVoucherQty(1); setOtherNumber("");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  if (!profile) return null;
  const pendingReq = requests.find((r) => r.status === "pending");

  const detailText = (c) => {
    if (c.type === "voucher") return `${c.voucher_qty || 1} × R${c.voucher_denomination || 100} ${c.voucher_type || "voucher"}`;
    if (c.type === "airtime") return `Airtime to ${c.recipient_number || "—"}`;
    return c.destination || c.type.toUpperCase();
  };

  return (
    <div className="space-y-5 animate-fade-up" data-testid="worker-cashout">
      <div>
        <h1 className="font-heading text-2xl font-bold">Cash out</h1>
        <p className="mt-1 text-sm text-muted-foreground">Available: <span className="font-bold text-primary">R{balance.toFixed(2)}</span></p>
      </div>

      {/* Payout details */}
      <div className="rounded-2xl border border-border/50 bg-card p-5 shadow-sm" data-testid="payout-details">
        <div className="flex items-center justify-between">
          <p className="font-heading font-bold">Payout details</p>
          <ChangeDetailsDialog profile={profile} onSubmitted={load} />
        </div>
        <div className="mt-3 space-y-2 text-sm">
          <div className="flex items-center gap-2"><Landmark className="h-4 w-4 text-primary" /> <span className="text-muted-foreground">Bank:</span> <span className="font-semibold">{profile.bank_name || "—"} {profile.bank_account ? `· ${profile.bank_account}` : ""}</span></div>
          <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /> <span className="text-muted-foreground">Phone:</span> <span className="font-semibold">{profile.phone || "—"}</span></div>
        </div>
        {pendingReq && <p className="mt-3 rounded-lg bg-brand-orange/10 px-3 py-2 text-xs font-medium text-brand-orange" data-testid="pending-change">Change request pending admin approval.</p>}
      </div>

      {/* Cashout form */}
      <form onSubmit={submit} className="space-y-4 rounded-2xl border border-border/50 bg-card p-5 shadow-sm">
        <div>
          <Label>Method</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="mt-1.5" data-testid="cashout-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{METHODS.map((m) => <SelectItem key={m.v} value={m.v} data-testid={`cashout-type-${m.v}`}>{m.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {isVoucher ? (
          <>
            <div>
              <Label>Voucher type</Label>
              <Select value={voucherType} onValueChange={setVoucherType}>
                <SelectTrigger className="mt-1.5" data-testid="voucher-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{VOUCHER_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Denomination</Label><Input value="R100" disabled className="mt-1.5" /></div>
              <div><Label>Quantity</Label><Input type="number" min="1" value={voucherQty} onChange={(e) => setVoucherQty(e.target.value)} data-testid="voucher-qty-input" className="mt-1.5" /></div>
            </div>
            <p className="rounded-lg bg-secondary px-3 py-2 text-sm" data-testid="voucher-total">Total: <span className="font-bold text-primary">R{computedAmount.toFixed(2)}</span></p>
          </>
        ) : isAirtime ? (
          <>
            <div>
              <Label>Recipient</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setAirtimeTarget("own")} data-testid="airtime-own" className={`rounded-xl border px-3 py-2.5 text-sm font-semibold ${airtimeTarget === "own" ? "border-primary bg-primary/5 text-primary" : "border-border"}`}>My number{profile.phone ? ` (${profile.phone})` : ""}</button>
                <button type="button" onClick={() => setAirtimeTarget("other")} data-testid="airtime-other" className={`rounded-xl border px-3 py-2.5 text-sm font-semibold ${airtimeTarget === "other" ? "border-primary bg-primary/5 text-primary" : "border-border"}`}>Another number</button>
              </div>
            </div>
            {airtimeTarget === "other" && <div><Label>Recipient number</Label><Input value={otherNumber} onChange={(e) => setOtherNumber(e.target.value)} data-testid="airtime-number-input" className="mt-1.5" placeholder="+27 …" /></div>}
            <div><Label>Airtime amount (R)</Label><Input type="number" min={MIN} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="airtime-amount-input" className="mt-1.5" /></div>
          </>
        ) : (
          <div>
            <Label>Amount (R) — max R{balance.toFixed(2)}</Label>
            <Input type="number" min={MIN} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cashout-amount-input" className="mt-1.5" />
            <p className="mt-1 text-xs text-muted-foreground">Minimum R{MIN}. Sent to your saved {type === "ewallet" ? "e-wallet number" : "bank account"}.</p>
          </div>
        )}

        <Button type="submit" disabled={busy || balance < MIN} data-testid="cashout-submit-button" className="w-full rounded-full bg-brand-navy font-semibold text-white hover:bg-brand-navy/90">
          {busy ? "Requesting…" : `Request cashout · R${computedAmount ? computedAmount.toFixed(2) : "0.00"}`}
        </Button>
      </form>

      {/* Redemption history */}
      <div>
        <h2 className="font-heading text-lg font-bold">Redemption history</h2>
        <div className="mt-3 space-y-2">
          {history.length === 0 && <p className="rounded-xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground" data-testid="cashout-empty">No cashouts yet.</p>}
          {history.map((c) => {
            const s = statusMap[c.status] || statusMap.pending;
            return (
              <div key={c.id} className="flex items-center justify-between rounded-xl border border-border/50 bg-card p-4 shadow-sm" data-testid="cashout-history-item">
                <div className="min-w-0">
                  <p className="font-heading font-bold">R{c.amount.toFixed(2)} <span className="text-xs font-normal text-muted-foreground">· {c.type.toUpperCase()}</span></p>
                  <p className="truncate text-xs text-muted-foreground">{detailText(c)} · {new Date(c.created_at).toLocaleDateString("en-ZA")}</p>
                </div>
                <Badge className={`gap-1 border-0 capitalize ${s.cls}`} data-testid={`redemption-status-${c.id}`}><s.icon className="h-3 w-3" /> {c.status}</Badge>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
